// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — cliente API PEMM-Proceso (Hammer · 0737)
// Consume /api/v1/process/madurez/pemm/*. Madurez de UN proceso: 13 habilitadores
// en 5 categorías × P1-P4, global = eslabón débil (MIN) calculado en backend.
// Espejo de api-madurez-proceso.ts (BPMM) pero para la escala PEMM (P1-P4).
// ════════════════════════════════════════════════════════════════════════════
import { request, getToken, BASE as API_ROOT } from '../../../shared/services/api';
import type { CategoriaPEMM, PNivel } from './descriptores-pemm-proceso';

export type NivelPemm = PNivel;
export type ColorProceso = 'VERDE' | 'AMARILLO' | 'ROJO';
export type EstadoEvalProceso = 'BORRADOR' | 'EN_REVISION' | 'APROBADA' | 'OBSOLETA';
export type TipoEjercicioProceso =
  | 'DIAGNOSTICO_INICIAL'
  | 'EVALUACION_PERIODICA'
  | 'POST_TRANSFORMACION'
  | 'AUDITORIA_EXTERNA';

/** Cabecera de una evaluación PEMM de un proceso. */
export interface EvaluacionPemm {
  id: string;
  procesoId: string;
  codigo: string | null;
  tipoEjercicio: TipoEjercicioProceso;
  fechaEvaluacion: string;
  nivelGlobal: NivelPemm | null;
  estado: EstadoEvalProceso;
  coberturaModelo?: string | null;
  observaciones?: string | null;
  /** Respuestas del cuestionario PEMM (0745): { `${hab}_qN`: { valor 1-4, nota? } }. */
  respuestas?: Record<string, { valor: number; nota?: string }> | null;
}

/** Evidencia ZENIC del habilitador (formato Hammer/máster · 0740). */
export interface ZenicPemm {
  cualitativa?: string;
  cuantitativa?: string;
  analisis?: string;
  leccion?: string;
}

/** Una celda del heatmap PEMM: el nivel asignado a un habilitador. */
export interface HabilitadorEvaluacionPemm {
  id: string;
  categoria: CategoriaPEMM;
  habilitador: string;
  nivel: NivelPemm;
  color: ColorProceso | null;
  observaciones: string | null;
  zenic?: ZenicPemm | null;
}

export interface HeatmapPemm {
  evaluacion: EvaluacionPemm;
  habilitadores: HabilitadorEvaluacionPemm[];
}

export interface SiembraPemm {
  habilitadores: HabilitadorEvaluacionPemm[];
  nivelGlobal: NivelPemm;
}

const BASE = '/process/madurez/pemm';

/** Lista las evaluaciones PEMM de un proceso (más reciente primero). */
export function fetchEvaluacionesPemm(procesoId: string) {
  return request<{ data: EvaluacionPemm[] }>(`${BASE}/procesos/${procesoId}/evaluaciones`);
}

/** Crea una evaluación PEMM en BORRADOR para un proceso. */
export function crearEvaluacionPemm(
  procesoId: string,
  body: { tipoEjercicio?: TipoEjercicioProceso; observaciones?: string } = {},
) {
  return request<{ data: EvaluacionPemm }>(`${BASE}/procesos/${procesoId}/evaluaciones`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Siembra los 13 habilitadores en P1 (suelo del audit experto). */
export function sembrarHabilitadoresPemm(evaluacionId: string) {
  return request<{ data: SiembraPemm }>(`${BASE}/evaluaciones/${evaluacionId}/sembrar`, {
    method: 'POST',
  });
}

/** Heatmap 13×4: cabecera + los 13 habilitadores con su nivel. */
export function fetchHeatmapPemm(evaluacionId: string) {
  return request<{ data: HeatmapPemm }>(`${BASE}/evaluaciones/${evaluacionId}/heatmap`);
}

/** Ajuste del consultor sobre un habilitador (P1-P4). Recalcula el global (eslabón débil). */
export function actualizarHabilitadorPemm(
  evaluacionId: string,
  body: {
    habilitador: string;
    nivel: NivelPemm;
    observaciones?: string | null;
    zenic?: ZenicPemm | null;
  },
) {
  return request<{ data: HabilitadorEvaluacionPemm }>(
    `${BASE}/evaluaciones/${evaluacionId}/habilitador`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/** Guarda una respuesta del cuestionario PEMM (0745) y devuelve el heatmap recomputado. */
export function guardarRespuestaPemm(
  evaluacionId: string,
  body: { preguntaClave: string; valor: number; nota?: string | null },
) {
  return request<{ data: HeatmapPemm }>(`${BASE}/evaluaciones/${evaluacionId}/respuesta`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Aprueba la evaluación (inmutable). Requiere rol directivo (enforced en backend). */
export function aprobarEvaluacionPemm(evaluacionId: string) {
  return request<{ data: EvaluacionPemm }>(`${BASE}/evaluaciones/${evaluacionId}/aprobar`, {
    method: 'POST',
  });
}

/** Conclusión del ingeniero de gobernanza (observaciones del header · 0738). */
export function guardarObservacionesPemm(evaluacionId: string, observaciones: string) {
  return request<{ data: EvaluacionPemm }>(`${BASE}/evaluaciones/${evaluacionId}/observaciones`, {
    method: 'PATCH',
    body: JSON.stringify({ observaciones }),
  });
}

// ── Análisis IA PEMM-nativo (0738) ────────────────────────────────────────────
export type EstadoAnalisisPemm = 'BORRADOR' | 'GENERADO' | 'VALIDADO';

export interface RecomendacionPemm {
  categoria: string;
  habilitador?: string;
  nivelGatillo?: NivelPemm | null;
  textoFinal: string;
  justificacion: string;
  prioridad: number;
}
export interface DafoPemm {
  fortalezas: string[];
  debilidades: string[];
  oportunidades: string[];
  amenazas: string[];
}
export interface SentinelFlagPemm {
  severity: string;
  message?: string;
}

// ── Hoja de Ruta (0741): iniciativas por fase + PICK + Kotter + DAFO cruces ────
export type PickPemm = 'IMPLEMENT' | 'CHALLENGE' | 'POSSIBLE' | 'KILL';
export type FaseRoadmap = 'FUNDAMENTOS' | 'CONSOLIDACION' | 'OPTIMIZACION';
export interface IniciativaPemm {
  id: string;
  titulo: string;
  fase: FaseRoadmap;
  brecha?: string;
  owner?: string;
  kpi?: string;
  pick: PickPemm;
  impacto: number;
  complejidad: number;
  kotter?: number[];
}
export interface RoadmapPemm {
  fases?: { clave: FaseRoadmap; nombre: string; objetivo?: string }[];
  iniciativas?: IniciativaPemm[];
  dafoCruces?: { fo: string[]; fa: string[]; do: string[]; da: string[] };
}

// ── Estudio de inversión TCO/ROI (máster Actividad 3 · 0744) ──────────────────
export interface BeneficioTco {
  concepto: string;
  importeAnual: number;
}
export interface TcoRoiPemm {
  horizonteAnios: number;
  costesDirectos: number[]; // por año
  costesIndirectos: number[]; // por año
  beneficios: BeneficioTco[];
  curvaAdopcion: number[]; // por año (0-1)
}

export interface AnalisisPemm {
  id: string;
  diagnosticoNarrativo: string;
  recomendaciones: RecomendacionPemm[];
  fortalezas: string[];
  riesgos: string[];
  conclusionEjecutiva: string | null;
  matrizDafo: DafoPemm | null;
  roadmap?: RoadmapPemm | null;
  tcoRoi?: TcoRoiPemm | null;
  estado: EstadoAnalisisPemm;
  modeloUsado: string | null;
  proveedor: string | null;
  sentinelFlags?: SentinelFlagPemm[] | null;
  creadoEn: string;
}

/** Guarda el estudio de inversión TCO/ROI (editado por el ingeniero). */
export function guardarTcoRoiPemm(analisisId: string, tcoRoi: TcoRoiPemm) {
  return request<{ data: AnalisisPemm }>(`${BASE}/analisis/${analisisId}/tco-roi`, {
    method: 'PATCH',
    body: JSON.stringify({ tcoRoi }),
  });
}

/** Genera (o regenera) el análisis IA PEMM-nativo de una evaluación. */
export function generarAnalisisPemm(evaluacionId: string) {
  return request<{ data: AnalisisPemm }>(`${BASE}/evaluaciones/${evaluacionId}/analisis`, {
    method: 'POST',
  });
}

/** Análisis IA vigente de una evaluación (o null). */
export function fetchAnalisisVigentePemm(evaluacionId: string) {
  return request<{ data: AnalisisPemm | null }>(`${BASE}/evaluaciones/${evaluacionId}/analisis`);
}

/** Validación humana del análisis (GENERADO→VALIDADO). Requiere rol directivo. */
export function validarAnalisisPemm(analisisId: string) {
  return request<{ data: AnalisisPemm }>(`${BASE}/analisis/${analisisId}/validar`, {
    method: 'POST',
  });
}

// ── Exportación / informe (descargas binarias · 0738 M3) ──────────────────────
// request() fuerza Content-Type JSON; las descargas usan fetch manual con Bearer.
async function descargarBlobPemm(path: string, fallbackName: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_ROOT}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Descarga el megaprompt .md (snapshot PEMM embebido) para claude.ai. */
export function descargarMegapromptPemm(evaluacionId: string): Promise<void> {
  return descargarBlobPemm(
    `${BASE}/evaluaciones/${evaluacionId}/megaprompt`,
    'megaprompt-madurez-pemm.md',
  );
}

/** Obtiene el informe HTML autónomo y devuelve un object URL blob (el llamador lo revoca). */
export async function obtenerInformePemmHtml(evaluacionId: string): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_ROOT}${BASE}/evaluaciones/${evaluacionId}/informe-html`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Descarga el informe Word (.docx) PEMM de un análisis VALIDADO+vigente (0739). */
export function descargarInformePemmWord(analisisId: string): Promise<void> {
  return descargarBlobPemm(`${BASE}/analisis/${analisisId}/informe`, 'informe-madurez-pemm.docx');
}

/** Descarga el exportador .py PEMM (genera el .docx desde claude.ai) de un análisis VALIDADO (0739). */
export function descargarExportadorPyPemm(analisisId: string): Promise<void> {
  return descargarBlobPemm(
    `${BASE}/analisis/${analisisId}/exportador-py`,
    'generar_informe_madurez_pemm.py',
  );
}
